import { z } from "zod";
import { resalePisCofinsTreatmentSchema } from "./calculation.js";
import { LOT_MAX_DATA_ROWS } from "./lot-mapping.js";
/*
 * Input corrections of an uploaded lot: what a person confirmed about rows of the upload after a
 * task calculated them, recorded with who, when and why. The upload, its tasks and their results
 * are never changed; a later task of the same upload (a recalculation) carries each correction in
 * its rows. The first kind is the PIS/COFINS treatment of rows the engine 1.8.0 catalog held back
 * (`REQUIRES_CONFIRMATION`: a condition the NCM cannot prove, such as "pão comum" under
 * Lei 10.925/2004, art. 1º, XVI; `OVERLAPPING_LISTS`: an NCM on more than one list, such as
 * 2106.90.10, whose Ex 01 is zero rated and whose Ex 02 is bebidas frias). A confirmation never
 * creates a zero rate: the API refuses a treatment the catalog does not back for the NCM, and the
 * engine keeps it pending anyway.
 */
/** Corrections one upload may hold; a lot has at most a few hundred distinct NCMs. */
export const LOT_INPUT_CORRECTION_MAX_PER_UPLOAD = 500;
export const lotInputCorrectionKindSchema = z.enum(["PIS_COFINS_TREATMENT"]);
const itemIdSchema = z.string().min(1).max(100);
/**
 * POST /v1/calculations/uploads/{uploadId}/input-corrections: confirm the PIS/COFINS treatment of
 * rows of one NCM that a finished task of the upload sent to review for confirmation
 * (REQUIRES_CONFIRMATION or OVERLAPPING_LISTS), whatever else keeps those rows in review.
 */
export const lotInputCorrectionRequestSchema = z
    .object({
    kind: z.literal("PIS_COFINS_TREATMENT"),
    /** The finished task whose results the person read; it is never changed. */
    sourceTaskId: z.string().uuid(),
    ncm: z.string().regex(/^\d{8}$/),
    treatment: resalePisCofinsTreatmentSchema,
    /** The rows confirmed, each pending confirmation with this NCM in the source task. */
    itemIds: z.array(itemIdSchema).min(1).max(LOT_MAX_DATA_ROWS),
    /** Why: kept with the record and the audit event. */
    reason: z.string().trim().min(1).max(500),
})
    .strict()
    .superRefine((input, context) => {
    if (new Set(input.itemIds).size !== input.itemIds.length) {
        context.addIssue({ code: "custom", path: ["itemIds"], message: "itemIds must be unique" });
    }
});
export const lotInputCorrectionSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    uploadId: z.string().uuid(),
    sourceTaskId: z.string().uuid(),
    kind: lotInputCorrectionKindSchema,
    ncm: z.string().regex(/^\d{8}$/),
    treatment: resalePisCofinsTreatmentSchema,
    /** The rows confirmed, with what the file said they are, so the record reads on its own. */
    items: z
        .array(z
        .object({
        itemId: itemIdSchema,
        sku: z.string().max(200).nullable(),
        description: z.string().max(300).nullable(),
    })
        .strict())
        .min(1)
        .max(LOT_MAX_DATA_ROWS),
    /** The statutory catalog entries that held the rows back (and back a zero treatment). */
    catalogEntryIds: z.array(z.string().min(1).max(120)).max(8),
    reason: z.string().min(1).max(500),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    requestId: z.string().min(1).max(128),
    correlationId: z.string().min(1).max(128),
})
    .strict();
/** GET /v1/calculations/uploads/{uploadId}/input-corrections: oldest first; the latest wins. */
export const lotInputCorrectionListSchema = z
    .object({
    items: z.array(lotInputCorrectionSchema).max(LOT_INPUT_CORRECTION_MAX_PER_UPLOAD),
})
    .strict();
/**
 * The rows with the corrections applied, latest correction first for each row: the treatment goes
 * on the item (and on its resale, which must agree). Only rows that ask for the statutory profiles
 * take a PIS/COFINS treatment (the contract requires `pisCofinsProfiles` for it). Pure.
 */
export function applyLotInputCorrections(items, corrections) {
    const treatment = new Map();
    for (const correction of corrections) {
        if (correction.kind !== "PIS_COFINS_TREATMENT")
            continue;
        for (const item of correction.items)
            treatment.set(item.itemId, { value: correction.treatment, id: correction.id });
    }
    const applied = new Set();
    const next = items.map((item) => {
        const found = treatment.get(item.itemId);
        if (!found || item.pisCofinsProfiles === undefined)
            return item;
        applied.add(found.id);
        return {
            ...item,
            pisCofinsTreatment: found.value,
            ...(item.resale ? { resale: { ...item.resale, pisCofinsTreatment: found.value } } : {}),
        };
    });
    return { items: next, applied: [...applied] };
}
//# sourceMappingURL=lot-input-corrections.js.map